import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getAdminOrganization, listAdminChannelConnections, listAdminInventoryConnections, listAdminOrganizationUsers, resetAdminOrganizationProviderQuota, updateAdminOrganizationProviderQuota } from '../../admin/adminApi'
import type { AdminConnectionSummary, AdminOrganizationSummary, AdminProviderProxySurface, AdminQuotaSummary, AdminUserSummary } from '../../admin/types'
import { Button, Card, I, Pill, gridColumns } from '../../components/ui'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport'
import { CodeValue, DataPill, EmptyState, ErrorState, LoadingState } from './adminPageHelpers'
import { formatDateTime, numberLabel, percentLabel, tableContainerStyle, tdStyle, thStyle } from './adminPageFormatters'

interface AdminOrganizationDetailPageProps {
  accessToken: string
  organizationId: number | null
  onBack: () => void
  onOpenProxy: (surface: AdminProviderProxySurface, organizationId: number, connectionId: number) => void
}

interface DetailState {
  organization: AdminOrganizationSummary
  users: AdminUserSummary[]
  channelConnections: AdminConnectionSummary[]
  inventoryConnections: AdminConnectionSummary[]
}

export function AdminOrganizationDetailPage({ accessToken, organizationId, onBack, onOpenProxy }: AdminOrganizationDetailPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [state, setState] = useState<DetailState | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [quotaBusyProvider, setQuotaBusyProvider] = useState<string | null>(null)
  const [quotaError, setQuotaError] = useState(false)

  const load = useCallback(function load() {
    if (organizationId == null) return
    setLoading(true)
    setError(false)
    Promise.all([
      getAdminOrganization(accessToken, organizationId),
      listAdminOrganizationUsers(accessToken, organizationId),
      listAdminChannelConnections(accessToken, organizationId),
      listAdminInventoryConnections(accessToken, organizationId),
    ])
      .then(([organization, users, channelConnections, inventoryConnections]) => setState({ organization, users, channelConnections, inventoryConnections }))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [accessToken, organizationId])

  useEffect(() => {
    void Promise.resolve().then(load)
  }, [load])

  const mergeQuota = (quota: AdminQuotaSummary) => {
    const mergeConnections = (connections: AdminConnectionSummary[]) => connections.map(connection =>
      connection.provider === quota.provider ? { ...connection, quota } : connection)
    setState(current => current ? {
      ...current,
      channelConnections: mergeConnections(current.channelConnections),
      inventoryConnections: mergeConnections(current.inventoryConnections),
    } : current)
  }
  const updateQuota = (provider: string, callLimit: number) => {
    if (organizationId == null) return
    setQuotaBusyProvider(provider)
    setQuotaError(false)
    updateAdminOrganizationProviderQuota(accessToken, organizationId, provider, callLimit)
      .then(mergeQuota)
      .catch(() => setQuotaError(true))
      .finally(() => setQuotaBusyProvider(null))
  }
  const resetQuota = (provider: string) => {
    if (organizationId == null) return
    setQuotaBusyProvider(provider)
    setQuotaError(false)
    resetAdminOrganizationProviderQuota(accessToken, organizationId, provider)
      .then(mergeQuota)
      .catch(() => setQuotaError(true))
      .finally(() => setQuotaBusyProvider(null))
  }

  if (organizationId == null) {
    return <ErrorState title={t('admin.organizationDetail.missing.title')} body={t('admin.organizationDetail.missing.body')} onRetry={onBack} />
  }
  if (loading) return <LoadingState label={t('admin.organizationDetail.loading')} />
  if (error || !state) return <ErrorState title={t('admin.organizationDetail.error.title')} body={t('admin.organizationDetail.error.body')} onRetry={load} />

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) auto'), gap: 14, alignItems: 'start' }}>
          <div>
            <Button kind="ghost" size="sm" onClick={onBack} icon={I.arrowL}>{t('admin.organizationDetail.back')}</Button>
            <h2 style={{ margin: '12px 0 4px', color: 'var(--brand-900)', fontSize: 26 }}>{state.organization.name}</h2>
            <div style={{ color: 'var(--fg-subtle)' }}><CodeValue>#{state.organization.id}</CodeValue></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(3, minmax(120px, 1fr))'), gap: 10 }}>
            <MiniStat label={t('admin.organizationDetail.stats.users')} value={numberLabel(state.users.length, i18n.language)} />
            <MiniStat label={t('admin.organizationDetail.stats.channels')} value={numberLabel(state.channelConnections.length, i18n.language)} />
            <MiniStat label={t('admin.organizationDetail.stats.inventory')} value={numberLabel(state.inventoryConnections.length, i18n.language)} />
          </div>
        </div>
      </Card>

      <Card>
        <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 20 }}>{t('admin.organizationDetail.users.title')}</h2>
        {state.users.length === 0 ? (
          <div style={{ marginTop: 12 }}><EmptyState title={t('admin.organizationDetail.users.emptyTitle')} body={t('admin.organizationDetail.users.emptyBody')} /></div>
        ) : (
          <div style={{ ...tableContainerStyle, marginTop: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>{t('admin.organizationDetail.users.table.name')}</th>
                  <th style={thStyle}>{t('admin.organizationDetail.users.table.role')}</th>
                  <th style={thStyle}>{t('admin.organizationDetail.users.table.status')}</th>
                  <th style={thStyle}>{t('admin.organizationDetail.users.table.locale')}</th>
                  <th style={thStyle}>{t('admin.organizationDetail.users.table.lastLogin')}</th>
                  <th style={thStyle}>{t('admin.organizationDetail.users.table.created')}</th>
                </tr>
              </thead>
              <tbody>
                {state.users.map(user => (
                  <tr key={user.id}>
                    <td style={{ ...tdStyle, color: 'var(--fg)' }}>
                      <div style={{ fontWeight: 800 }}>{user.fullName}</div>
                      <div style={{ color: 'var(--fg-subtle)', overflowWrap: 'anywhere' }}>{user.email}</div>
                    </td>
                    <td style={tdStyle}><DataPill value={user.role} /></td>
                    <td style={tdStyle}><DataPill value={user.status} /></td>
                    <td style={tdStyle}>{t(`common.locales.${user.locale}`, { defaultValue: user.locale })}</td>
                    <td style={tdStyle}>{formatDateTime(user.lastLoginAt, i18n.language, t('admin.common.none'))}</td>
                    <td style={tdStyle}>{formatDateTime(user.createdAt, i18n.language, t('admin.common.none'))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConnectionSection
        title={t('admin.organizationDetail.channelConnections.title')}
        emptyTitle={t('admin.organizationDetail.channelConnections.emptyTitle')}
        emptyBody={t('admin.organizationDetail.channelConnections.emptyBody')}
        surface="channel"
        organizationId={state.organization.id}
        connections={state.channelConnections}
        onOpenProxy={onOpenProxy}
        quotaBusyProvider={quotaBusyProvider}
        onUpdateQuota={updateQuota}
        onResetQuota={resetQuota}
      />
      <ConnectionSection
        title={t('admin.organizationDetail.inventoryConnections.title')}
        emptyTitle={t('admin.organizationDetail.inventoryConnections.emptyTitle')}
        emptyBody={t('admin.organizationDetail.inventoryConnections.emptyBody')}
        surface="inventory"
        organizationId={state.organization.id}
        connections={state.inventoryConnections}
        onOpenProxy={onOpenProxy}
        quotaBusyProvider={quotaBusyProvider}
        onUpdateQuota={updateQuota}
        onResetQuota={resetQuota}
      />
      {quotaError && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{t('admin.organizationDetail.connections.quotaEditor.error')}</div>}
    </div>
  )
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 12 }}>
      <div style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
      <div style={{ marginTop: 5, color: 'var(--brand-900)', fontWeight: 900, fontSize: 22 }}>{value}</div>
    </div>
  )
}

function ConnectionSection({ title, emptyTitle, emptyBody, surface, organizationId, connections, onOpenProxy, quotaBusyProvider, onUpdateQuota, onResetQuota }: {
  title: string
  emptyTitle: string
  emptyBody: string
  surface: AdminProviderProxySurface
  organizationId: number
  connections: AdminConnectionSummary[]
  onOpenProxy: (surface: AdminProviderProxySurface, organizationId: number, connectionId: number) => void
  quotaBusyProvider: string | null
  onUpdateQuota: (provider: string, callLimit: number) => void
  onResetQuota: (provider: string) => void
}) {
  const { t, i18n } = useTranslation()

  return (
    <Card>
      <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 20 }}>{title}</h2>
      {connections.length === 0 ? (
        <div style={{ marginTop: 12 }}><EmptyState title={emptyTitle} body={emptyBody} /></div>
      ) : (
        <div style={{ ...tableContainerStyle, marginTop: 14 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>{t('admin.organizationDetail.connections.table.connection')}</th>
                <th style={thStyle}>{t('admin.organizationDetail.connections.table.status')}</th>
                <th style={thStyle}>{t('admin.organizationDetail.connections.table.health')}</th>
                <th style={thStyle}>{t('admin.organizationDetail.connections.table.quota')}</th>
                <th style={thStyle}>{t('admin.organizationDetail.connections.table.latestActivity')}</th>
                <th style={thStyle}>{t('admin.common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {connections.map(connection => (
                <tr key={connection.id}>
                  <td style={{ ...tdStyle, color: 'var(--fg)' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <strong>{connection.displayName}</strong>
                      <Pill tone="info" size="sm">{connection.provider}</Pill>
                    </div>
                    <div style={{ marginTop: 4, color: 'var(--fg-subtle)' }}><CodeValue>#{connection.id}</CodeValue>{connection.externalAccountId ? ` · ${connection.externalAccountId}` : ''}</div>
                  </td>
                  <td style={tdStyle}><DataPill value={connection.status} /></td>
                  <td style={tdStyle}>
                    <DataPill value={connection.health?.status} />
                    {connection.health?.lastErrorCode && <div style={{ marginTop: 5 }}><CodeValue>{connection.health.lastErrorCode}</CodeValue></div>}
                  </td>
                  <td style={tdStyle}>
                    <QuotaEditor
                      key={`${connection.provider}-${connection.quota?.callLimit ?? 'none'}`}
                      connection={connection}
                      busy={quotaBusyProvider === connection.provider}
                      onSave={onUpdateQuota}
                      onReset={onResetQuota}
                    />
                  </td>
                  <td style={tdStyle}>{formatDateTime(connection.latestActivityAt, i18n.language, t('admin.common.none'))}</td>
                  <td style={tdStyle}><Button kind="ghost" size="sm" onClick={() => onOpenProxy(surface, organizationId, connection.id)}>{t('admin.organizationDetail.connections.openProxy')}</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function QuotaEditor({ connection, busy, onSave, onReset }: {
  connection: AdminConnectionSummary
  busy: boolean
  onSave: (provider: string, callLimit: number) => void
  onReset: (provider: string) => void
}) {
  const { t, i18n } = useTranslation()
  const [value, setValue] = useState(connection.quota?.callLimit == null ? '' : String(connection.quota.callLimit))
  const parsed = Number(value)
  const valid = value.trim() !== '' && Number.isInteger(parsed) && parsed >= 0

  return (
    <div style={{ display: 'grid', gap: 7, minWidth: 180 }}>
      <div>{connection.quota ? `${connection.quota.callsUsed}/${connection.quota.callLimit} · ${percentLabel(connection.quota.usageRatio, i18n.language)}` : t('admin.common.none')}</div>
      <label style={{ display: 'grid', gap: 4, color: 'var(--fg-subtle)', fontSize: 12 }}>
        {t('admin.organizationDetail.connections.quotaEditor.label', { provider: connection.provider })}
        <input
          type="number"
          min="0"
          step="1"
          value={value}
          onChange={event => setValue(event.target.value)}
          style={{ width: '100%', minWidth: 0, padding: '7px 9px', border: '1px solid var(--border-default)', borderRadius: 10, color: 'var(--fg)', background: 'white', font: 'inherit' }}
        />
      </label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Button kind="secondary" size="xs" disabled={!valid || busy} onClick={() => onSave(connection.provider, parsed)}>
          {busy ? t('admin.organizationDetail.connections.quotaEditor.saving') : t('admin.organizationDetail.connections.quotaEditor.save')}
        </Button>
        <Button kind="ghost" size="xs" disabled={busy} onClick={() => onReset(connection.provider)}>
          {t('admin.organizationDetail.connections.quotaEditor.useDefault')}
        </Button>
      </div>
    </div>
  )
}
